import { createContext, useEffect, useRef, useState } from "react"

import axios from "axios" 

import {
  signIn,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword
} from 'aws-amplify/auth'
import { Hub } from '@aws-amplify/core'
import { Amplify } from 'aws-amplify'

import { axiosInstance, axiosManager } from '../service/axiosManager'
import {
  signInWithEmailAndPassword as signInWithBackend,
  requestPasswordResetCode,
  confirmPasswordResetWithCode
} from '../service/authService'

// Sign-in checks at page load. Under load (a whole class opening the link at once) they can
// time out or fail with a 5xx; that is not the same as being signed out. Retry with jittered
// backoff - about a minute in all - unless `isFinal(status)` says the answer is definitive.
const AUTH_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 15000, 15000]
const withRetries = async (request, isFinal) => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await request()
    } catch (error) {
      const status = error?.response?.status
      if (isFinal(status) || attempt >= AUTH_RETRY_DELAYS_MS.length) throw error
      const delay = AUTH_RETRY_DELAYS_MS[attempt] * (0.75 + Math.random() * 0.5)
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }
}

export const AuthContext = createContext()

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: 'us-east-2_W8iffbshV',
      userPoolClientId: '4l70fghng5q9ncjm3npcs4pokk'
    }
  }
})

//Context for authentication
export const AuthContextProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null)
  const [currentUserLoading, setCurrentUserLoading] = useState(true)
  const [passwordResetState, setPasswordResetState] = useState({
    email: '',
    isCodeSent: false,
    error: null
  })
  // Each checkAuth, sign-out and sign-up takes a new number. A check that retries for a minute
  // (withRetries) must not land after a later sign-in or sign-out and undo it, so it only
  // writes while its number is still the latest.
  const authCheck = useRef(0)

  useEffect(() => {
    checkAuth()

    const listener = Hub.listen('auth', ({ payload }) => {
      const { event } = payload
      if (event === 'signedIn') {
        checkAuth()
      } else if (event === 'signedOut') {
        authCheck.current++
        setCurrentUser(null)
        setCurrentUserLoading(false)
        localStorage.removeItem('token') // Cleanup guest user auth token
      }
    })

    return () => {
      listener()
    }
  }, [])

  const checkAuth = async () => {
    const check = ++authCheck.current
    const superseded = () => authCheck.current !== check
    setCurrentUserLoading(true)
    try {
      try {
        const awsUser = await getCurrentUser()
        const session = await fetchAuthSession()
        if (superseded()) return null

        // If AWS recognizes the user, sync the token and load profile
        if (session.tokens?.accessToken) {
          axiosManager.setAuthToken(session.tokens.accessToken.toString())
        }

        // A busy server is not "no such user": retried (see withRetries).
        const userInfo = await withRetries(
          () => axiosInstance.get(`${process.env.REACT_APP_HOST_API}/users/uid/${awsUser.userId}`),
          (status) => status >= 400 && status < 500
        ).then((response) => response.data?.data, () => null)
        if (superseded()) return null
        if (userInfo) {
          console.log('AWS User restored:', userInfo.email)
          setCurrentUser(userInfo)
          setCurrentUserLoading(false)
          return userInfo
        }
      } catch (awsError) {
        // If AWS auth fails, we next try to check if guest token exists
      }

      const guestToken = localStorage.getItem('token')

      if (guestToken) {
        try {
          // Verify this token with backend
          const response = await withRetries(
            () => axios.post(process.env.REACT_APP_HOST_API + '/validate', { token: guestToken }, { timeout: 10000 }),
            (status) => status === 401 || status === 404
          )
          if (superseded()) return null

          if (response.status === 200 && response.data) {
            console.log("Guest User restored:", response.data.first_name)

            // Restore the session
            axiosManager.setAuthToken(guestToken)
            setCurrentUser(response.data)
            setCurrentUserLoading(false)
            return response.data
          }
        } catch (validateError) {
          if (superseded()) return null
          // Only a token the server rejects (401) or whose user is gone (404) is dropped. Any
          // other failure is the server being busy or unreachable: deleting the token then
          // sent the guest back to /login, where joining again made a new user and left their
          // work behind. Keep it - the next load restores them.
          const status = validateError?.response?.status
          if (status === 401 || status === 404) {
            console.warn("Guest token was invalid or expired:", validateError.message)
            localStorage.removeItem('token')
          } else {
            console.warn("Could not reach the server to restore the guest sign-in:", validateError.message)
          }
        }
      }

      // Both failed. The user is truly logged out.
      throw new Error('No active session found')

    } catch (finalError) {
      if (superseded()) return null
      // Final Cleanup
      console.log('User is not logged in.')
      setCurrentUser(null)
      setCurrentUserLoading(false)
      axiosManager.clearAuthToken()
      return null
    }
  }

  const handleSignIn = async (email, password) => {
    try {
      try {
        await signOut()
        axiosManager.clearAuthToken()
      } catch (error) {
        console.log('Sign out error:', error)
      }

      try {
        const result = await signIn({ username: email, password })
        const user = await checkAuth()
        if (!user) {
          throw new Error('Your account signed in, but no VizPI profile was found.')
        }
        return { ...result, user }
      } catch (cognitoError) {
        // Fall back to VizPI's own password check (POST /login). This must also happen on
        // NotAuthorizedException: the Cognito pool hides which emails exist, so an account
        // that was never in Cognito (every instructor, students created in the database)
        // gets that same "Incorrect username or password" error, never UserNotFoundException.
        // Still safe after a Cognito password change: the backend refuses any account that
        // has a Cognito user (authService.validatePassword), so an old local hash can't log
        // in.
        try {
          const token = await signInWithBackend(email, password)
          localStorage.setItem('token', token)
          axiosManager.setAuthToken(token)
          const user = await checkAuth()
          if (!user) throw new Error('Unable to restore your VizPI profile.')
          return { isSignedIn: true, user }
        } catch (backendError) {
          // Both said no: report Cognito's answer so the message doesn't depend on which
          // store holds the account.
          if (cognitoError?.name === 'NotAuthorizedException') throw cognitoError
          throw backendError?.response ? backendError : cognitoError
        }
      }
    } catch (error) {
      console.error('Sign in error:', error)
      throw error
    }
  }

  const handleSignUp = async (userInfo) => {
    const response = await axiosInstance.post(process.env.REACT_APP_HOST_API + '/signup', {
      user: userInfo,
    })
    if (response.status === 200) {
      // Log the new account in immediately when the backend returns a token (see
      // authService.signUp) - previously this just returned data with nothing setting
      // currentUser, so a caller had to separately sign in right after signing up.
      if (response.data?.token && response.data?.user) {
        authCheck.current++
        localStorage.setItem('token', response.data.token)
        axiosManager.setAuthToken(response.data.token)
        setCurrentUser(response.data.user)
        // The page-load check this supersedes no longer ends the loading state.
        setCurrentUserLoading(false)
      }
      return response.data
    } else {
      throw new Error(response.data.message)
    }
  }

  const handleSignOut = async () => {
    authCheck.current++
    try {
      // Attempt AWS Signout
      try {
        await signOut()
      } catch (error) {
        // Ignore errors (e.g., if user was a guest and not logged into AWS)
      }

      // Clear Context
      setCurrentUser(null)
      setCurrentUserLoading(false)
      axiosManager.clearAuthToken()

      // Clear Guest Storage
      localStorage.removeItem('token')
      localStorage.removeItem('guestUser')

    } catch (error) {
      console.error('Error signing out:', error)
      throw error
    }
  }

  const handleForgotPassword = async (email) => {
    try {
      try {
        // Cognito emails the code for accounts it holds.
        await resetPassword({ username: email })
      } catch (cognitoError) {
        // Cognito refuses a sixth request for the same email for a long while (over 10 minutes,
        // measured 2026-10-29), even when it holds no such account. For an account the backend
        // emails, that refusal says nothing, so the backend is still asked; its own limit (one
        // code a minute) then decides.
        const cognitoLimited = cognitoError?.name === 'LimitExceededException' || cognitoError?.name === 'TooManyRequestsException'
        if (cognitoError?.name !== 'UserNotFoundException' && !cognitoLimited) throw cognitoError
      }
      // Always ask the backend too: the Cognito pool hides which emails exist, so for an
      // account that only lives in VizPI's database (every instructor, students created
      // there) resetPassword above "succeeds" without sending anything. The backend emails a
      // code only for those accounts, never for Cognito ones, so nobody gets two emails.
      await requestPasswordResetCode(email)
      setPasswordResetState({
        email,
        isCodeSent: true,
        error: null
      })
      return { success: true }
    } catch (error) {
      console.error('Forgot password error:', error)
      const errorMessage = error?.name === 'LimitExceededException' || error.response?.status === 429
        ? 'Please wait a bit before requesting another code.'
        : 'An error occurred while processing your request.'

      setPasswordResetState({
        email: '',
        isCodeSent: false,
        error: errorMessage
      })
      throw error
    }
  }

  const handleConfirmPasswordReset = async (email, code, newPassword) => {
    try {
      try {
        await confirmResetPassword({
          username: email,
          confirmationCode: code,
          newPassword
        })
      } catch (cognitoError) {
        // Not a Cognito account (see handleForgotPassword): the code came from the backend.
        try {
          await confirmPasswordResetWithCode(email, code, newPassword)
        } catch (backendError) {
          throw cognitoError?.name === 'UserNotFoundException' ? backendError : cognitoError
        }
      }

      setPasswordResetState({
        email: '',
        isCodeSent: false,
        error: null
      })

      return { success: true }
    } catch (error) {
      console.error('Confirm password reset error:', error)
      const invalidCode = error?.name === 'CodeMismatchException'
        || error?.name === 'ExpiredCodeException'
        || error.response?.data?.message === 'Invalid or expired code'
      const errorMessage = invalidCode
        ? 'Invalid or expired code. Please try again.'
        : error?.name === 'InvalidPasswordException'
          ? (error.message || 'Password does not meet requirements')
          : 'An error occurred while resetting your password.'

      setPasswordResetState(prev => ({
        ...prev,
        error: errorMessage
      }))
      throw error
    }
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        currentUserLoading,
        setCurrentUserLoading,
        handleSignIn,
        handleSignUp,
        handleSignOut,
        handleForgotPassword,
        handleConfirmPasswordReset,
        passwordResetState
      }}>
      {children}
    </AuthContext.Provider>
  )
}
